import React, { FC, useState } from 'react';

import { apiOrganizationDeleteVerificationAccountFile } from 'src/api/organization';
import { UiButton } from 'src/components/UI/Button';
import { UiModal } from 'src/components/UI/modal/UiModal';
import { UiOuterLink } from 'src/components/UI/OuterLink';
import { UiCleanBaseImageUploader } from 'src/components/UI/uploaders/CleanBaseImageUploader';
import { S3UploadReturnType } from 'src/utils/aws-s3';
import { useRequest } from 'src/utils/hooks/useRequest';
import { useNsTHelper } from 'src/utils/i18n';
import { useStateForm } from 'src/utils/stateForm';

type Props = {
  files: {
    ID: number;
    Uri: string;
    Name: string;
  }[];
  canDelete: boolean;
  locale: string;
  onFileDeleted: (fileInfo: Props['files'][0]) => void;
  onFileAdded: (data: S3UploadReturnType) => void;
  canUpload: boolean;
  title: string;

  fileNamePrefix?: string;
};

export const OrganizationOldVerifyFilesPartial: FC<Props> = ({
  files,
  canDelete,
  locale,
  onFileDeleted,
  onFileAdded,
  canUpload,
  fileNamePrefix,
  title,
}) => {
  const tHelper = useNsTHelper(locale, 'filesSection');

  const formProps = useStateForm();

  const { apiCaller } = useRequest();

  const [modalInfo, setModalInfo] = useState<Props['files'][0] | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const onDeleteConfirm = (file: Props['files'][0]) => () => {
    setModalInfo(file);
  };

  return (
    <div className="text-left margin-top-10">
      <span>{title}</span>

      <div className="margin-top-15 margin-bottom-10">
        <UiCleanBaseImageUploader
          formOnChange={formProps.onChange}
          name="anyname"
          suggestedHeight={1920}
          suggestedWidth={1080}
          fileNamePrefix={fileNamePrefix}
          onChange={onFileAdded}
          noCropper
          multiple
          render={({ onStartChoosingFile, uploading }) => (
            <UiButton
              primary
              primaryNew
              className="no-margin"
              disabled={!canUpload || uploading}
              onClick={onStartChoosingFile}
            >
              {tHelper('uploadBtnText')}
            </UiButton>
          )}
        />
        <span className="margin-left-10">{tHelper('uploadDescription')}</span>
      </div>

      {files.map((file) => (
        <div className="row" key={file.Uri}>
          <div
            className="col-sm-8"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <i className="material-icons cursor-auto">attach_file</i>&nbsp;
            <span>{file.Name}</span>
          </div>
          <div className="col-sm-2">
            <UiButton
              tag="a"
              className="anchor-default text-uppercase"
              onClick={onDeleteConfirm(file)}
              disabled={!canDelete}
            >
              {tHelper('deleteBtnText')}
            </UiButton>
          </div>
          <div className="col-sm-2">
            <UiOuterLink href={file.Uri} className="anchor-default text-uppercase" download>
              {tHelper('downloadBtnText')}
            </UiOuterLink>
          </div>
        </div>
      ))}

      {modalInfo !== null && (
        <UiModal
          onHide={() => setModalInfo(null)}
          header={tHelper('deleteModal.header')}
          successBtnText={tHelper('deleteModal.successBtnText')}
          loading={deleteLoading}
          onSuccess={() => {
            if (modalInfo.ID === 0) {
              onFileDeleted(modalInfo);
            } else {
              setDeleteLoading(true);

              apiCaller(apiOrganizationDeleteVerificationAccountFile, {
                FileID: modalInfo.ID,
              })
                .then(() => {
                  onFileDeleted(modalInfo);

                  setModalInfo(null);
                })
                .finally(() => {
                  setDeleteLoading(false);
                });
            }
          }}
        >
          {tHelper('deleteModal.body')}
        </UiModal>
      )}
    </div>
  );
};
